import PropTypes from "prop-types";
import { Pagination } from "@aqar-app/aqar-design-system";
import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Circle as RCCircle } from "rc-progress";
import { addApolloState, initApolloClient } from "../../../lib/apolloClient";
import { buildPath, getDistrictReviewLevel } from "../../../lib/helpers";
import { SERVICE_RATES_QUERY } from "../../../graphql/queries/rating";
import CardContainer from "../../../components/cardContainer/cardContainer";
import ReviewItem from "../../../components/review-item/ReviewItem";
import styles from "../districtReview.module.scss";
import SEO from "@/components/seo";

const SERIVICES_TO_DISPLAY = {
  electricity_water_rate: "الكهرباء والماء",
  internet_rate: "الإنترنت",
  streets_rate: "الشوارع والمرافق",
  medical_services_rate: "الخدمات الطبية",
  educational_services_rate: "الخدمات التعليمية",
};

export default function DistrictReview({
  initServiceReviews,
  districtId,
  pathLevel,
  city,
}) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data } = useQuery(SERVICE_RATES_QUERY, {
    variables: {
      district_id: districtId,
      offset: (currentPage - 1) * 50,
    },
  });

  const serviceReviews = data?.Rating?.getDistrictRate || initServiceReviews;
  const buildTitle = () => {
    return `تقييم ${pathLevel?.districtName?.replace(/-/g, " ")}`;
  };

  const getPercentage = (rate) => {
    return ((rate / 5) * 100).toFixed(2);
  };

  const getStrokeColor = (percentage) => {
    if (percentage >= 80) {
      return "#52b44a";
    }
    if (percentage >= 50) {
      return "#f5ce45";
    }
    return "#eb605a";
  };

  const pageChangeHandler = (pgNbr) => {
    setCurrentPage(pgNbr);
  };

  const renderPagination = () => {
    const paginationProps = {
      total: serviceReviews?.rate_count || 0,
      currentPage,
      itemsPerPage: 50, // TODO: move this to config constants
      onPageChange: pageChangeHandler,
    };
    return <Pagination {...paginationProps} />;
  };

  const desc = useMemo(() => {
    return Object.keys(SERIVICES_TO_DISPLAY).reduce((acc, key) => {
      const percentage = getPercentage(serviceReviews?.[key]);
      if (!percentage) return acc + "";
      return acc + SERIVICES_TO_DISPLAY[key] + `: ${percentage}%,`;
    }, "");
  }, [serviceReviews]);
  const renderServicesRate = () => {
    return Object.entries(SERIVICES_TO_DISPLAY).map(([key, label]) => {
      const percentage = getPercentage(serviceReviews[key]);
      return (
        <div className={styles.reviewWrapper}>
          <div className={styles.item}>
            <RCCircle
              percent={percentage}
              strokeWidth={6}
              trailWidth={6}
              strokeColor={getStrokeColor(percentage)}
            />
            <span className={styles.percentage}>{percentage}%</span>
          </div>
          <p>{label}</p>
        </div>
      );
    });
  };

  const renderUserReviews = () => {
    if (serviceReviews?.list?.length) {
      return (
        <>
          <h3>{`تقييم وآراء سكان ${pathLevel?.districtName?.replace(
            /-/g,
            " ",
          )} (${serviceReviews?.rate_count})`}</h3>
          {serviceReviews?.list?.map((review) => (
            <ReviewItem key={review.id} {...review} />
          ))}
          {renderPagination()}
        </>
      );
    }
    return null;
  };

  return (
    <>
      <SEO
        title={`${buildTitle()} | تقييم الأحياء في السعودية | عقار`}
        description={`تقييم ${pathLevel?.districtName?.replace(
          /-/g,
          " ",
        )} ، ${city}, ${desc}`}
        keywords={[
          "تقييم الأحياء",
          "تقييم الأحياء في السعودية",
          pathLevel?.districtName,
          pathLevel?.directionName,
          city,
          "عقار",
          "Aqar",
        ]
          .filter((k) => k)
          .join(",")}
      ></SEO>

      <CardContainer
        title="تقييم احياء السعودية"
        cardClassName={styles.districtReviewCard}
        cardContainerClassName={styles.districtReviewContainer}
        breadcrumbItems={[
          {
            name: "تقييم احياء السعودية",
            path: "/district-reviews",
          },
          {
            name: city.replace(/-/g, " "),
            path: `/district-reviews/${city}`,
          },
          {
            name: pathLevel?.districtName?.replace(/-/g, " "),
            path: `/district-reviews/${buildPath({
              path: "{cityName}/{directionName}/{districtName}",
              pathParams: { ...pathLevel, cityName: city },
            })}`,
          },
        ]}
      >
        <h2 className={styles.subTitle}>{buildTitle()}</h2>
        {serviceReviews && (
          <div className={styles.serviceReviewsWrapper}>
            {renderServicesRate()}
          </div>
        )}
        {renderUserReviews()}
      </CardContainer>
    </>
  );
}
DistrictReview.propTypes = {
  initServiceReviews: PropTypes.shape({
    rate: PropTypes.number,
    rate_count: PropTypes.number,
    electricity_water_rate: PropTypes.number,
    internet_rate: PropTypes.number,
    streets_rate: PropTypes.number,
    medical_services_rate: PropTypes.number,
    educational_services_rate: PropTypes.number,
    list: {
      id: PropTypes.number,
      created_time: PropTypes.number,
      rate: PropTypes.number,
      comment: PropTypes.string,
    },
  }),
  districtId: PropTypes.number.isRequired,
  pathLevel: PropTypes.shape({
    level: PropTypes.string,
    directionName: PropTypes.string,
    districtName: PropTypes.string,
  }).isRequired,
  city: PropTypes.string.isRequired,
};

DistrictReview.defaultProps = {
  initServiceReviews: null,
};

export async function getServerSideProps({ params, req }) {
  const { getCachedObject } = await import("../../../graphql/queries/regions");

  const { city, params: pathParams } = params;

  const pathLevel = getDistrictReviewLevel(pathParams);
  const districtPath = `/${buildPath({
    path: "{cityName}/{directionName}/{districtName}",
    pathParams: { ...pathLevel, cityName: city },
  })}`;

  const apolloClient = initApolloClient();
  const { district_id } =
    getCachedObject(undefined, "DistrictType", districtPath) || {};

  const isCSRouting = !req || (req.url && req.url.startsWith("/_next/data"));
  if (isCSRouting) {
    return {
      props: {
        districtId: district_id,
        city,
        pathLevel,
      },
    };
  }

  const districtServiceRateRequest = await apolloClient.query({
    query: SERVICE_RATES_QUERY,
    variables: {
      district_id,
      offset: 0,
    },
  });
  // TODO: handle failing requests here and route to the right error page

  return addApolloState(apolloClient, {
    props: {
      initServiceReviews:
        districtServiceRateRequest?.data?.Rating?.getDistrictRate || null,
      districtId: district_id,
      city,
      pathLevel,
    },
  });
}
